import { useState, useRef, useEffect, useCallback } from 'react';

/**
 * <InlineEditable> — click (or double-click) to edit a text value in place.
 *
 * Pattern:
 * - Single-click selects (parent owns selection), double-click enters edit.
 * - Enter / blur → onSave(newValue). Esc → cancels.
 * - Empty submit reverts to original (don't allow empty titles via inline).
 *
 * Props:
 *   value      — current string
 *   onSave     — async (newValue) => any. Should throw to signal failure.
 *   className  — outer class for the display element (so it inherits parent typography)
 *   editClassName — outer class for the input
 *   testId     — `${testId}-display` + `${testId}-input`
 *   maxLength  — char cap (default 240)
 *   editMode   — 'dblclick' (default) | 'click'
 *   placeholder — shown when value empty
 *   ariaLabel  — accessible label for the input
 *   disabled   — when true, renders as static text
 */
export function InlineEditable({
  value,
  onSave,
  className = '',
  editClassName = '',
  testId = 'inline-edit',
  maxLength = 240,
  editMode = 'dblclick',
  placeholder = 'Untitled',
  ariaLabel = 'Edit title',
  disabled = false,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);
  const committedRef = useRef(false);

  useEffect(() => {
    if (!editing) setDraft(value || '');
  }, [value, editing]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const startEdit = useCallback(() => {
    if (disabled) return;
    committedRef.current = false;
    setEditing(true);
  }, [disabled]);

  const commit = useCallback(async () => {
    if (committedRef.current) return;
    committedRef.current = true;
    const trimmed = draft.trim();
    if (!trimmed || trimmed === (value || '').trim()) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(trimmed);
      setEditing(false);
    } catch {
      // Surface failure by keeping editor open and reverting committed flag
      committedRef.current = false;
    } finally {
      setSaving(false);
    }
  }, [draft, value, onSave]);

  const cancel = useCallback(() => {
    committedRef.current = true;
    setDraft(value || '');
    setEditing(false);
  }, [value]);

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      commit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      cancel();
    }
    // Don't bubble — many parent cards have their own Enter/Space handlers
    e.stopPropagation?.();
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        data-testid={`${testId}-input`}
        type="text"
        value={draft}
        maxLength={maxLength}
        disabled={saving}
        aria-label={ariaLabel}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={commit}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        className={`bg-transparent outline-none border-b w-full ${editClassName}`}
        style={{ borderColor: 'var(--pd-focus)' }}
        placeholder={placeholder}
      />
    );
  }

  const dblHandler = editMode === 'dblclick'
    ? {
        // Single click intentionally bubbles — the parent owns selection
        // (e.g., opening the task editor). Double-click enters inline edit.
        onDoubleClick: (e) => { e.stopPropagation(); startEdit(); },
      }
    : {
        onMouseDown: (e) => { e.stopPropagation(); },
        onClick: (e) => { e.stopPropagation(); startEdit(); },
      };

  return (
    <span
      data-testid={`${testId}-display`}
      {...dblHandler}
      role={disabled ? undefined : 'button'}
      tabIndex={disabled ? undefined : -1}
      aria-label={disabled ? undefined : `${ariaLabel} (double-click)`}
      className={`${className} ${disabled ? '' : 'pd-inline-editable cursor-text relative'}`}
      title={disabled ? undefined : 'Double-click to edit'}
    >
      {value && value.trim() ? value : <span style={{ color: 'var(--pd-text-tertiary)', fontStyle: 'italic' }}>{placeholder}</span>}
      {!disabled ? (
        <span aria-hidden="true" className="pd-inline-editable-hint" />
      ) : null}
    </span>
  );
}

export default InlineEditable;
